/**
 * GMA-1617 (CompassCare Web 2/14 — Overview) — pure dashboard model.
 *
 * The command-center dashboard answers "who is impacted, what changed, how
 * serious, where, what next" from a client/alert dataset. This module holds the
 * UI-free logic the Overview page renders: alert prioritization, client-status
 * severity ranking, and the filter-chip predicates.
 *
 * Presentational and fail-safe: a malformed row never throws (one bad record
 * can't break the dashboard render), matching webFormat's contract. The
 * severity vocabulary comes from the single design-token set (SEVERITY_LEVELS),
 * so this stays in lockstep with the rest of the facelift.
 */
import { formatDuration, toEpochMs } from "../../utils/webFormat";

// Urgency rank for prioritization (higher = more urgent). NOT the SEVERITY_LEVELS
// array order — there `nora` (informational) is last but must rank LOW for
// urgency. Every SEVERITY_LEVELS key has an entry here; the contract test pins
// that lockstep so a new severity can't be silently unranked.
const SEVERITY_URGENCY = Object.freeze({
  critical: 4,
  atRisk: 3,
  caution: 2,
  normal: 1,
  nora: 0,
});

// A client can also be "offline" (a connectivity state, not a clinical
// severity, so it is not in SEVERITY_LEVELS). It is surfaced as its own filter.
export const OFFLINE_STATUS = "offline";

/**
 * Urgency rank for a severity level. Unknown / missing -> -1 so unrecognized
 * values sort below every known severity rather than throwing.
 * @param {*} level
 * @returns {number}
 */
export function severityUrgency(level) {
  return Object.prototype.hasOwnProperty.call(SEVERITY_URGENCY, level)
    ? SEVERITY_URGENCY[level]
    : -1;
}


/**
 * Order alerts for the Active Alerts rail: most urgent first (by severity),
 * then oldest first within the same severity (longer-elapsed = more pressing).
 * Pure and non-mutating; tolerates a non-array input and malformed rows.
 *
 * Alert shape (presentational): { severity, createdAt } where createdAt is an
 * ISO string / epoch-ms / Date. A row with no parseable time sorts after timed
 * rows of the same severity (we cannot prove its age, so it is not "oldest").
 *
 * @param {Array} alerts
 * @param {number} [now=Date.now()] reference for age; kept for testability
 * @returns {Array} a new, sorted array
 */
export function sortAlertsBySeverityThenAge(alerts, now = Date.now()) {
  const list = Array.isArray(alerts) ? alerts.slice() : [];
  const ref = typeof now === "number" && Number.isFinite(now) ? now : Date.now();
  return list.sort((a, b) => {
    const ua = severityUrgency(a && a.severity);
    const ub = severityUrgency(b && b.severity);
    if (ua !== ub) return ub - ua; // higher urgency first
    // Same severity: older first. Missing time -> treated as `ref` (age 0), so
    // undated rows fall after dated ones of equal severity.
    const ta = toEpochMs(a && a.createdAt);
    const tb = toEpochMs(b && b.createdAt);
    return (ta === null ? ref : ta) - (tb === null ? ref : tb);
  });
}

// Overview filter chips (client status grid). `all` is the default.
export const CLIENT_FILTERS = Object.freeze([
  "all",
  "critical",
  "atRisk",
  "caution",
  "offline",
  "groupHomes",
  "independent",
]);

// Each chip -> predicate over a client. Living-environment chips are orthogonal
// to status chips (a group-home client can also be critical), so they filter on
// `livingEnv`, not `status`.
const FILTER_PREDICATES = Object.freeze({
  all: () => true,
  critical: (c) => c.status === "critical",
  atRisk: (c) => c.status === "atRisk",
  caution: (c) => c.status === "caution",
  offline: (c) => c.status === OFFLINE_STATUS,
  groupHomes: (c) => c.livingEnv === "group_home",
  independent: (c) => c.livingEnv === "independent",
});

/**
 * Filter the client status grid by a chip. Unknown filter -> the full list
 * (fail-open: never hide clients because of a bad filter value). Tolerates a
 * non-array input and null rows.
 * @param {Array} clients
 * @param {string} [filter="all"]
 * @returns {Array}
 */
export function filterClients(clients, filter = "all") {
  const list = Array.isArray(clients) ? clients : [];
  // Own-property lookup only: a hostile key like "__proto__" would otherwise
  // resolve to Object.prototype (truthy, non-function) and throw when called,
  // breaking the no-throw contract. Unknown/hostile keys fall through to "all".
  const predicate = Object.prototype.hasOwnProperty.call(FILTER_PREDICATES, filter)
    ? FILTER_PREDICATES[filter]
    : undefined;
  if (typeof predicate !== "function" || filter === "all") {
    return list.filter(Boolean);
  }
  return list.filter((c) => c && predicate(c));
}

/**
 * GMA-1617: derive the Overview's 7 KPI-strip tile descriptors from a dataset.
 *
 * A pure transform — decoupled from data fetching. Counts it can derive from
 * the roster (clients monitored, vitals out of range) and live feeds (active
 * alerts, devices offline) are computed here; the remaining metrics whose data
 * source is out of this module's scope (today's fall/SOS events, average
 * response time, environment risks) are passed through pre-computed and fall
 * back to the "—" placeholder when absent, so the strip renders regardless.
 *
 * Tiles that meaningfully scope the client grid carry a `filter` (a
 * CLIENT_FILTERS key); the rest are informational (KpiStrip renders them
 * non-interactive). Fail-safe: null/malformed inputs never throw.
 *
 * @param {object} [datasets]
 * @param {Array}  [datasets.clients]              roster ({ status, ... })
 * @param {Array}  [datasets.alerts]               active alerts
 * @param {Array}  [datasets.devices]              ({ online: boolean })
 * @param {number} [datasets.todaysEventCount]     falls/SOS today (pre-counted)
 * @param {number} [datasets.avgResponseSeconds]   avg response time, seconds
 * @param {number} [datasets.environmentRiskCount] active environment risks
 * @returns {Array<{key,label,value,unit?,severity?,filter?}>}
 */
export function computeKpis(datasets = {}) {
  const d = datasets || {};
  const clients = Array.isArray(d.clients) ? d.clients.filter(Boolean) : [];
  const alerts = Array.isArray(d.alerts) ? d.alerts.filter(Boolean) : [];
  const devices = Array.isArray(d.devices) ? d.devices.filter(Boolean) : [];

  const vitalsOutOfRange = clients.filter((c) => c.status === "atRisk").length;
  const devicesOffline = devices.filter((v) => v.online === false).length;
  const activeAlerts = alerts.length;

  // Tint the Active Alerts tile by the MOST-URGENT active alert's severity, not
  // merely "any alert" — a set of only caution alerts must not read as critical.
  // Start below every severity so the first real alert sets the tint (nora, the
  // lowest-urgency valid level, still counts); only a zero-alert set stays the
  // benign "normal".
  let topAlertSeverity = "normal";
  let topUrgency = -Infinity;
  for (const a of alerts) {
    const u = severityUrgency(a && a.severity);
    if (u > topUrgency) {
      topUrgency = u;
      topAlertSeverity = a && a.severity ? a.severity : "normal";
    }
  }

  const asNum = (v) =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  const todaysEvents = asNum(d.todaysEventCount);
  const environmentRisks = asNum(d.environmentRiskCount);
  const avgSeconds = asNum(d.avgResponseSeconds);

  const PLACEHOLDER = "—";

  return [
    {
      key: "clientsMonitored",
      label: "Clients Monitored",
      value: clients.length,
      filter: "all",
    },
    {
      key: "activeAlerts",
      label: "Active Alerts",
      value: activeAlerts,
      severity: topAlertSeverity,
    },
    {
      key: "todaysEvents",
      label: "Today's Events",
      value: todaysEvents === null ? PLACEHOLDER : todaysEvents,
    },
    {
      key: "avgResponse",
      label: "Avg Response",
      value: avgSeconds === null ? PLACEHOLDER : formatDuration(avgSeconds),
    },
    {
      key: "vitalsOutOfRange",
      label: "Vitals Out of Range",
      value: vitalsOutOfRange,
      severity: vitalsOutOfRange > 0 ? "atRisk" : "normal",
      filter: "atRisk",
    },
    {
      key: "devicesOffline",
      label: "Devices Offline",
      value: devicesOffline,
      filter: "offline",
    },
    {
      key: "environmentRisks",
      label: "Environment Risks",
      value: environmentRisks === null ? PLACEHOLDER : environmentRisks,
    },
  ];
}
